import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '@/api/http';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/dashboard/mensagens/')({ component: MensagensListPage });

function MensagensListPage() {
  const { data: threads = [], isLoading } = useQuery({
    queryKey: ['chat-threads'],
    queryFn: () => api.get('chat/threads').json<any[]>(),
    refetchInterval: 15000,
  });

  return (
    <div className="max-w-4xl space-y-5">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900">Mensagens</h1>
        <p className="text-slate-500 text-sm mt-1">Conversas com seus profissionais e clientes vinculados</p>
      </div>

      <PushNotificationCard />

      {isLoading ? (
        <p className="text-slate-500 text-sm">Carregando...</p>
      ) : threads.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-10 text-center">
          <p className="font-bold text-slate-700">Nenhuma conversa ainda</p>
          <p className="text-sm text-slate-500 mt-2">
            Quando você agendar uma sessão, poderá conversar com a outra pessoa por aqui.
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {threads.map((t: any) => (
            <li key={`${t.clientId}-${t.therapistId}`}>
              <Link
                to="/dashboard/mensagens/thread"
                search={{ clientId: t.clientId, therapistId: t.therapistId } as any}
                className={cn(
                  'flex items-center gap-4 p-4 rounded-xl bg-white border border-slate-200 hover:border-teal-300 hover:shadow-sm transition cursor-pointer',
                  t.unread > 0 && 'border-teal-300 bg-teal-50/30',
                )}
              >
                {t.peer?.image ? (
                  <img src={t.peer.image} alt="" className="size-12 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="size-12 rounded-full bg-gradient-to-br from-teal-300 to-coral-300 flex items-center justify-center text-white font-bold shrink-0">
                    {(t.peer?.name ?? '?').charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-semibold text-slate-800 truncate">{t.peer?.name ?? 'Conversa'}</p>
                    {t.lastMessageAt && (
                      <span className="text-xs text-slate-400 shrink-0 whitespace-nowrap">
                        {formatDateShort(t.lastMessageAt)}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 truncate mt-0.5">
                    {t.lastMessage ?? 'Nova conversa'}
                  </p>
                </div>
                {t.unread > 0 && (
                  <span className="bg-coral-500 text-white text-[10px] font-bold rounded-full min-w-5 h-5 flex items-center justify-center px-1.5 shrink-0">
                    {t.unread}
                  </span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PushNotificationCard() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      setEnabled(false);
      return;
    }
    setEnabled(Notification.permission === 'granted');
  }, []);

  if (enabled === null) return null;
  if (enabled) return null;
  if (typeof window !== 'undefined' && !('Notification' in window)) return null;

  async function activate() {
    setBusy(true);
    try {
      const result = await Notification.requestPermission();
      setEnabled(result === 'granted');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-slate-800 text-sm">Notificações no navegador</p>
        <p className="text-xs text-slate-500 mt-0.5">
          Receba avisos de novos agendamentos e lembretes mesmo com a aba fechada.
        </p>
      </div>
      <Button onClick={activate} disabled={busy} className="bg-teal-500 hover:bg-teal-600 text-white">
        {busy ? 'Aguarde...' : 'Ativar'}
      </Button>
    </div>
  );
}

function formatDateShort(d: string | Date): string {
  const date = new Date(d);
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }).replace('.', '');
}
